import './ShipmentShowLogistic.scss';

import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { DateField, TextField } from '@refinedev/antd';
import { Button, Flex, Form, Input, message, Modal, Space, Tooltip } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EditableModal, { useEditableModal } from '@/components/general/EditableModal';
import LinkTextField from '@/components/general/LinkTextField';
import {
  EditableInfoItem,
  ShowInfoGrid,
  ShowInfoItem,
} from '@/components/general/ShowInfo/ShowInfo';
import { ShowSubsection } from '@/components/general/ShowInfo/ShowSection';
import UploadedFileList from '@/components/general/UploadFile/UploadedFileList';
import UploadFileComponent from '@/components/general/UploadFile/UploadFile';
import { useCountryContext } from '@/contexts/CountryContext';
import { useUserContext } from '@/contexts/UserContext';
import FileData from '@/model/General/FileData';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { downloadFile } from '@/utils/downloadFile';

import { getShippingLabelFileCode, getShippingLabelFilePhotos } from '../shipmentTaxFileUtils';
import ModifyDeliveryNumbersModal from './ModifyDeliveryNumbersModal';
import ShipmentShowPackages from './ShipmentShowPackages';

interface ShipmentShowLogisticProps {
  record?: Shipment;
  refetch?: () => void;
  setBlockChangeState?: (block: { id: string; message: string }[]) => void;
}

const ShipmentShowLogistic = (props: ShipmentShowLogisticProps) => {
  const { t } = useTranslation();
  const { userWorkArea } = useUserContext();
  const { isBrasilSelected } = useCountryContext();
  const { shipmentRepository, packageRepository } = useRepositoriesContext();
  const { activeModal, openModal, closeModal } = useEditableModal<
    'tracking' | 'delivery' | 'shipment_price'
  >();
  const { record, refetch, setBlockChangeState } = props;
  const showPackageInfo = isBrasilSelected && record?.status !== PACKAGE_STATES.PENDIENTE;
  const canEditLogisticData =
    userWorkArea === USER_WORK_AREA.LOGISTICS && record?.status !== PACKAGE_STATES.FINALIZADO;
  const showShipmentDetails =
    record?.status === PACKAGE_STATES.PREPARACION ||
    record?.status === PACKAGE_STATES.PREPARADO ||
    record?.status === PACKAGE_STATES.EN_VIAJE ||
    record?.status === PACKAGE_STATES.FINALIZADO;
  // --- Estado para etiqueta de envío ---
  const [labelUploadFiles, setLabelUploadFiles] = useState<FileData[]>([]);
  const [labelUploadModalOpen, setLabelUploadModalOpen] = useState(false);
  /** Metadata: extrae el nombre real del archivo desde una URL, descartando query params */
  const getFileNameFromUrl = (url: string): string =>
    decodeURIComponent(url.split('?')[0].split('/').pop() ?? 'etiqueta-envio');
  /**
   * Se hidratan los archivos de etiqueta existentes desde record.photos
   * filtrando por el código de descripción shipping_label_file_{id}.
   */
  const existingLabelFiles = useMemo(() => {
    if (!record?.id || !record?.photos) return [];
    const labelPhotos = getShippingLabelFilePhotos(record.photos, record.id);

    return labelPhotos.map(
      f =>
        new FileData({
          id: f.id,
          title: getFileNameFromUrl(f.photo),
          file: f.photo,
        }),
    );
  }, [record?.id, record?.photos]);
  /**
   * Metadata: handler de eliminación de etiqueta de envío.
   * Marca el archivo como removido y lo elimina vía packageRepository.saveFiles.
   */
  const handleDeleteLabelFile = useCallback(
    (file: FileData) => {
      Modal.confirm({
        title: t('¿Estás seguro de que deseas eliminar esta etiqueta de envío?'),
        content: t('Esta acción no se puede deshacer'),
        okText: t('Sí, eliminar'),
        okType: 'danger',
        cancelText: t('No'),
        onOk: () => {
          if (!record?.id) return;
          const fileToRemove = new FileData({ id: file.id, title: file.title, file: file.fileUrl });

          fileToRemove.setRemoved(true);
          void packageRepository.saveFiles([fileToRemove], record.id).then(() => {
            void refetch?.();
          });
        },
      });
    },
    [t, record?.id, packageRepository, refetch],
  );

  return (
    <div className="ShipmentShowLogisticData">
      {showPackageInfo && (
        <ShowSubsection title={t('Detalles del paquete')}>
          <ShipmentShowPackages
            record={record}
            refetch={refetch}
            setBlockChangeState={setBlockChangeState}
          />
        </ShowSubsection>
      )}

      {showShipmentDetails && (
        <ShowSubsection title={t('Detalles del envío')}>
          <ShowInfoGrid>
            <EditableInfoItem
              label={t('Número de seguimiento')}
              action={
                canEditLogisticData && (
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => openModal('tracking')}
                  />
                )
              }>
              <TextField value={record?.tracking_number ?? '-'} />
            </EditableInfoItem>

            {record?.tracking_number && record?.shipper?.tracking_link && (
              <ShowInfoItem label={t('Seguimiento')}>
                <LinkTextField
                  to={`${record?.shipper?.tracking_link}/${record?.tracking_number}`}
                  label={t('Ver seguimiento')}
                  external
                />
              </ShowInfoItem>
            )}
            <EditableInfoItem
              label={t('Números de remito')}
              action={
                canEditLogisticData && (
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => openModal('delivery')}
                  />
                )
              }>
              <DeliveryNotesList record={record} />
            </EditableInfoItem>
            {/* --- Sección de etiqueta de envío ---
             * Permite cargar una etiqueta de envío (ej. rótulo, shipping label)
             * que se guarda como foto del paquete con description = shipping_label_file_{id}.
             * La carga se habilita solo para logística cuando el envío no está finalizado. */}
            {isBrasilSelected && (
              <>
                <ShowInfoItem label={t('Etiqueta de envío')}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {existingLabelFiles.length > 0 ? (
                      existingLabelFiles.map(file => (
                        <Space key={file.id} size="small" align="center">
                          <FileOutlined />
                          <a
                            href={file.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ wordBreak: 'break-all' }}>
                            {file.title}
                          </a>
                          <Tooltip title={t('Descargar')}>
                            <Button
                              icon={<DownloadOutlined />}
                              size="small"
                              type="text"
                              onClick={() => void downloadFile(file)}
                            />
                          </Tooltip>
                          {canEditLogisticData && (
                            <Tooltip title={t('Eliminar')}>
                              <Button
                                icon={<DeleteOutlined />}
                                size="small"
                                type="text"
                                danger
                                onClick={() => handleDeleteLabelFile(file)}
                              />
                            </Tooltip>
                          )}
                        </Space>
                      ))
                    ) : (
                      <Flex gap={8} wrap>
                        <TextField value={t('No hay etiqueta cargada')} />
                        {canEditLogisticData && (
                          <Button
                            icon={<UploadOutlined />}
                            size="small"
                            disabled={existingLabelFiles.length > 0}
                            onClick={() => setLabelUploadModalOpen(true)}
                          />
                        )}
                      </Flex>
                    )}
                  </div>
                  {canEditLogisticData && (
                    <Modal
                      title={t('Cargar etiqueta de envío')}
                      open={labelUploadModalOpen}
                      onCancel={() => {
                        setLabelUploadModalOpen(false);
                        setLabelUploadFiles([]);
                      }}
                      okText={t('Cargar')}
                      cancelText={t('Cancelar')}
                      okButtonProps={{ disabled: labelUploadFiles.length === 0 }}
                      onOk={() => {
                        Modal.confirm({
                          title: t('¿Estás seguro de que deseas cargar esta etiqueta de envío?'),
                          okText: t('Sí'),
                          okType: 'primary',
                          cancelText: t('No'),
                          onOk: () => {
                            if (!record?.id) {
                              setLabelUploadModalOpen(false);
                              setLabelUploadFiles([]);

                              return;
                            }

                            const labelCode = getShippingLabelFileCode(record.id);
                            // Limitar a un solo archivo de etiqueta
                            const filesToUpload = labelUploadFiles.slice(0, 1);

                            void packageRepository
                              .saveFiles(filesToUpload, record.id, labelCode)
                              .then(() => {
                                setLabelUploadFiles([]);
                                setLabelUploadModalOpen(false);
                                void refetch?.();
                              });
                          },
                        });
                      }}>
                      <UploadFileComponent
                        fileList={labelUploadFiles}
                        setFileList={(updater: any) => {
                          // Limitar a máximo 1 archivo en la lista
                          setLabelUploadFiles(prev => {
                            const result = typeof updater === 'function' ? updater(prev) : updater;

                            if (result.length > 1) {
                              message.warning(t('Solo se permite una etiqueta de envío'));

                              return result.slice(-1);
                            }

                            return result;
                          });
                        }}
                      />
                      {labelUploadFiles.length > 0 && (
                        <div style={{ marginTop: 16 }}>
                          <UploadedFileList
                            fileList={labelUploadFiles}
                            setFileList={setLabelUploadFiles}
                          />
                        </div>
                      )}
                    </Modal>
                  )}
                </ShowInfoItem>
                <EditableInfoItem
                  label={t('Precio del envío')}
                  action={
                    canEditLogisticData && (
                      <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => openModal('shipment_price')}
                      />
                    )
                  }>
                  <TextField
                    value={record?.shipment_price ? `R$ ${record?.shipment_price}` : '-'}
                  />
                </EditableInfoItem>
              </>
            )}
          </ShowInfoGrid>
        </ShowSubsection>
      )}

      {(record?.date || record?.date_finished) && (
        <ShowSubsection title={t('Fechas')}>
          <ShowInfoGrid>
            {record?.date && (
              <ShowInfoItem label={t('Fecha de envío efectiva')}>
                {record?.date ? <DateField value={record?.date} /> : '-'}
              </ShowInfoItem>
            )}
            {record?.date_finished && (
              <ShowInfoItem label={t('Fecha de recepción del cliente')}>
                {record?.date_finished ? <DateField value={record?.date_finished} /> : '-'}
              </ShowInfoItem>
            )}
          </ShowInfoGrid>
        </ShowSubsection>
      )}

      <EditableModal
        activeModal={activeModal}
        closeModal={closeModal}
        modalConfigByKey={{
          tracking: {
            title: t('Editar número de seguimiento'),
            form: {
              initialValues: { trackingNumber: record?.tracking_number },
              onFinish: async values => {
                if (!record?.id || values.trackingNumber === record?.tracking_number) {
                  return false;
                }

                await shipmentRepository.updateShipment(record?.id, {
                  tracking_number: values.trackingNumber,
                });
                void props.refetch?.();
              },
              children: (
                <Form.Item name="trackingNumber">
                  <Input />
                </Form.Item>
              ),
            },
          },
          delivery: {
            title: t('Editar número de remito'),
            content: (
              <ModifyDeliveryNumbersModal
                record={record}
                refetch={props.refetch}
                closeModal={closeModal}
              />
            ),
          },
          shipment_price: {
            title: t('Editar precio del envío'),
            form: {
              initialValues: { shipmentPrice: record?.shipment_price },
              onFinish: async values => {
                if (!record?.id || values.shipmentPrice === record?.shipment_price) {
                  return false;
                }

                await shipmentRepository.updateShipment(record?.id, {
                  shipment_price: values.shipmentPrice,
                });
                void props.refetch?.();
              },
              children: (
                <Form.Item name="shipmentPrice">
                  <Input prefix="R$" />
                </Form.Item>
              ),
            },
          },
        }}
      />
    </div>
  );
};

/**
 * Componente auxiliar que renderiza la lista de números de remito.
 * Se mantiene en este archivo porque es simple y solo se usa aquí.
 */
const DeliveryNotesList = ({ record }: { record?: Shipment }) => {
  if (!record?.delivery_note) return <>-</>;

  return (
    <ul className="ShipmentShowValueList">
      {record?.delivery_note?.split(',').map((number, index) => {
        const deliveryLink = record?.odoo_delivery_link?.[index];

        return deliveryLink ? (
          <li key={`${number}-${deliveryLink}`}>
            <LinkTextField external to={deliveryLink} label={number} />
          </li>
        ) : (
          <li key={number}>
            <div className="DeliveryNumberItem">{number} </div>
          </li>
        );
      })}
    </ul>
  );
};

export default ShipmentShowLogistic;
